import type { OperationItem } from '@travel-planner/shared/schemas/operations';
import { isSameDay } from 'date-fns';
import { toUtcDate } from './office-time';

// Ported from legacy travel_planner_front/app/container/OperationsPageContainer/utils.ts:27-50
// Keys match v_itinerary_item.contentCategory / contentSubcategory string values.
export const ALLOWED_PARENTS: Record<string, readonly string[]> = {
  Transportation: [
    'Destination',
    'Activity',
    'Attraction',
    'Accommodation',
    'Dining',
    'Venue',
    'Ferry',
    'Flight',
    'Train',
    'Cruise',
  ],
  Ticket: ['Activity', 'Attraction', 'Venue'],
  Guide: ['Activity', 'Attraction', 'Venue'],
};

const lookupAllowed = (item: OperationItem): readonly string[] => {
  const bySubcategory = item.contentSubcategory
    ? ALLOWED_PARENTS[item.contentSubcategory]
    : undefined;
  if (bySubcategory) return bySubcategory;
  const byCategory = item.contentCategory
    ? ALLOWED_PARENTS[item.contentCategory]
    : undefined;
  return byCategory ?? [];
};

const matchesAllowed = (
  candidate: OperationItem,
  allowed: readonly string[]
): boolean =>
  allowed.some(
    (type) =>
      type === candidate.contentCategory ||
      type === candidate.contentSubcategory
  );

export const canBeSubitem = (item: OperationItem): boolean =>
  lookupAllowed(item).length > 0;

// Returns root items that could host `item` as a sub-item: same day, allowed
// category/subcategory, not the item itself, not a sub-item already.
export const getAllowedParents = (
  item: OperationItem,
  candidates: readonly OperationItem[]
): OperationItem[] => {
  const allowed = lookupAllowed(item);
  if (allowed.length === 0) return [];
  if (!item.startTime) return [];
  // Parse Postgres timestamp strings as UTC so same-day grouping doesn't
  // drift by the viewer's timezone (`isSameDay` compares calendar days).
  const itemDate = toUtcDate(item.startTime);
  return candidates.filter((candidate) => {
    if (!candidate.id || candidate.id === item.id) return false;
    if (candidate.parentId) return false;
    if (!candidate.startTime) return false;
    if (!isSameDay(toUtcDate(candidate.startTime), itemDate)) return false;
    return matchesAllowed(candidate, allowed);
  });
};
